import {ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ActiveCartFacade, Cart} from '@spartacus/cart/base/root';
import {CheckoutOrderSummaryComponent} from '@spartacus/checkout/base/components';
import {FeatureTogglesService} from '@dcp/configurator';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {CommonError} from '@app/model/error/common-error.model';
import {TemplateView} from '@app/model/template-view.model';
import {CartDataService} from '../services/cart-data.service';
import {CheckoutEntryValidationService} from '../services/checkout-entry-validation.service';

const TAX_CALCULATION_ERROR_CODES = ['DCPP_009', 'DCPP_010', 'DCPP_011'];

@UntilDestroy()
@Component({
  selector: 'dcp-checkout-order-summary',
  templateUrl: './dcp-checkout-order-summary.component.html',
  styleUrls: ['./dcp-checkout-order-summary.component.scss'],
})
export class DcpCheckoutOrderSummaryComponent extends CheckoutOrderSummaryComponent implements OnInit {
  @Input() selectedView: TemplateView;
  view = TemplateView;
  cartContainsError: boolean = false;
  checkoutEstimatedTaxTooltipToggle: boolean = false;
  taxNotCalculatedErrorDisplayToggle: boolean = false;
  isTaxEnabled: boolean = false;
  isEstimatedTaxTitleEnabled: boolean = false;
  foundTaxErrorSeverity: string = '';

  stripeMessageDesign: {[key: string]: string} = {
    warning: 'Warning',
    error: 'Negative',
    info: 'Information',
  };

  constructor(
    protected checkoutEntryValidationService: CheckoutEntryValidationService,
    private togglesService: FeatureTogglesService,
    protected cdr: ChangeDetectorRef,
    private cartDataService: CartDataService,
    protected activeCartFacade: ActiveCartFacade,
  ) {
    super(activeCartFacade);
  }

  ngOnInit(): void {
    this.checkCartForErrors();
    this.subscribeToEstimatedTaxTitleToggle();
    this.isTaxEnabled = this.selectedView === this.view.CheckOut;
    this.isEstimatedTaxTitleEnabled =
      this.checkoutEstimatedTaxTooltipToggle &&
      this.selectedView === this.view.CheckOut;
    this.subscribeToTaxErrorToggle();
    this.taxNotCalculatedErrorDisplayToggle =
      this.taxNotCalculatedErrorDisplayToggle &&
      this.selectedView === this.view.CheckOut;
  }

  subscribeToEstimatedTaxTitleToggle(): void {
    this.togglesService
      .isEnabled(UNLEASH_TOGGLES.checkoutEstimatedTaxTooltip)
      .pipe(untilDestroyed(this))
      .subscribe((isEnabled) => {
        this.checkoutEstimatedTaxTooltipToggle = isEnabled;
      });
  }

  subscribeToTaxErrorToggle(): void {
    this.togglesService
      .isEnabled(UNLEASH_TOGGLES.taxNotCalculatedErrorDisplay)
      .pipe(untilDestroyed(this))
      .subscribe((isEnabled) => {
        this.taxNotCalculatedErrorDisplayToggle = isEnabled;
      });
  }

  totalTitle(cart: Cart): string {
    let title = 'total';
    if (cart?.totalPrice?.value === 0) {
      title = 'initialCost';
    } else if (this.isTaxEnabled) {
      title = 'totalWithTax';
    }
    return title;
  }

  getTotalPrice(cart: Cart) {
    return cart?.[this.isTaxEnabled ? 'totalPriceWithTax' : 'totalPrice']
      ?.formattedValue;
  }

  getTaxErrorsSeverity(cart: Cart): string {
    const error: CommonError = this.cartDataService.getErrorDetails(cart);
    return TAX_CALCULATION_ERROR_CODES.includes(error?.errorCode) ? error.severity : '';
  }

  private checkCartForErrors() {
    this.cart$.pipe(untilDestroyed(this)).subscribe((cart) => {
      this.cartContainsError =
        this.checkoutEntryValidationService.isCartInvalid(cart);
      this.foundTaxErrorSeverity = this.getTaxErrorsSeverity(cart);
      this.cdr.detectChanges();
    });
  }

  getFormattedSeverity(severity: string): string | undefined {
    const formattedSeverity = this.stripeMessageDesign[severity.toLowerCase()];
    return formattedSeverity ? formattedSeverity : undefined;
  }
}
